if (!customElements.get('header-mobile-search')) {
  customElements.define(
    'header-mobile-search',
    class HeaderMobileSearch extends HTMLElement {
      constructor() {
        super();

        this.boundOnTriggerClick = this.onTriggerClick.bind(this);
        this.boundOnTriggerPointerDown = this.onTriggerPointerDown.bind(this);
        this.boundClose = this.close.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('form[is="predictive-search"]');
        this.triggers = document.querySelectorAll('[aria-controls="HeaderMobileSearch"]');
        this.closeButton = this.querySelector('[data-header-mobile-search-close]');

        this.triggers.forEach((trigger) => {
          trigger.addEventListener('pointerdown', this.boundOnTriggerPointerDown);
          trigger.addEventListener('touchstart', this.boundOnTriggerPointerDown, { passive: false });
          trigger.addEventListener('click', this.boundOnTriggerClick);
        });

        this.closeButton?.addEventListener('click', this.boundClose);
      }

      disconnectedCallback() {
        this.triggers?.forEach((trigger) => {
          trigger.removeEventListener('pointerdown', this.boundOnTriggerPointerDown);
          trigger.removeEventListener('touchstart', this.boundOnTriggerPointerDown);
          trigger.removeEventListener('click', this.boundOnTriggerClick);
        });

        this.closeButton?.removeEventListener('click', this.boundClose);
      }

      onTriggerPointerDown(event) {
        if (window.matchMedia('(min-width: 1024px)').matches) return;

        // iOS keyboard opens reliably only when focus happens in a direct touch/pointer gesture.
        event.preventDefault();
        this.open();
      }

      onTriggerClick(event) {
        if (window.matchMedia('(min-width: 1024px)').matches) return;

        event.preventDefault();
        if (document.body.classList.contains('header-mobile-search-open')) return;
        this.open();
      }

      open() {
        const header = this.closest('header');

        if (header) {
          this._headerLockedMinHeight = header.getBoundingClientRect().height;
        }

        document.body.classList.add('header-mobile-search-open');
        this.removeAttribute('hidden');

        if (header && this._headerLockedMinHeight) {
          header.style.minHeight = `${this._headerLockedMinHeight}px`;
        }

        this.triggers.forEach((trigger) => {
          trigger.setAttribute('aria-expanded', 'true');
        });

        // iOS requires focus to happen right after the user gesture to open the keyboard reliably.
        const input = this.form?.input;
        if (input) {
          try {
            input.focus({ preventScroll: true });
          } catch (e) {
            input.focus();
          }

          // Put caret at the end.
          try {
            const len = input.value.length;
            input.setSelectionRange(len, len);
          } catch (e) {}
        }

        window.requestAnimationFrame(() => {
          this.form?.setHeaderSearchOffset?.();

          // If iOS ignored the first focus attempt, retry once after layout.
          if (input && document.activeElement !== input) {
            try {
              input.focus({ preventScroll: true });
            } catch (e) {
              input.focus();
            }
          }
        });
      }

      close() {
        document.body.classList.remove('header-mobile-search-open');
        document.body.classList.remove('predictive-search-open');

        if (this.form) {
          this.form.input.value = '';
          this.form.removeAttribute('results');
          this.form.removeAttribute('loading');
          this.form.clearHeaderSearchOffset?.();
        }

        this.setAttribute('hidden', '');

        this.triggers.forEach((trigger) => {
          trigger.setAttribute('aria-expanded', 'false');
        });

        const header = this.closest('header');

        if (header) {
          header.style.minHeight = '';
          header.setHeight?.();
        }
      }
    }
  );
}

if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();

        this.addEventListener('toggle', this.onToggle.bind(this));
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }

      onToggle() {
        if (this.hasAttribute('open')) return;

        window.requestAnimationFrame(() => {
          // Ensure body scroll lock classes don't get stuck after closing search.
          const hasOpenLayer = document.querySelector('.drawer[open], modal-dialog[open], quick-view[open]');

          if (!hasOpenLayer) {
            document.body.classList.remove('has-modal-open', 'has-modal-opening');
          }

          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.focusElement = this.input;
        this.boundOnDocumentClick = this.onDocumentClick.bind(this);
        this.onHeaderSearchResize = () => {
          if (document.body.classList.contains('predictive-search-open')) {
            this.setHeaderSearchOffset();
          }
        };

        if (this.resetButton) {
          this.resetButton.addEventListener('click', this.clear.bind(this));
        }
        this.input.addEventListener('click', this.onInputClick.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
        this.addEventListener('click', this.onTabClick.bind(this));
      }

      connectedCallback() {
        if (this.isInlineHeaderSearch) {
          window.addEventListener('resize', this.onHeaderSearchResize);
          document.addEventListener('click', this.boundOnDocumentClick, true);
        }
      }

      disconnectedCallback() {
        if (this.isInlineHeaderSearch) {
          window.removeEventListener('resize', this.onHeaderSearchResize);
          document.removeEventListener('click', this.boundOnDocumentClick, true);
        }
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.querySelector('.typed');
      }

      get isHeaderSearch() {
        return this.classList.contains('search__form--header');
      }

      get isMobileHeaderSearch() {
        return this.classList.contains('search__form--header-mobile');
      }

      get isInlineHeaderSearch() {
        return this.isHeaderSearch || this.isMobileHeaderSearch;
      }

      setHeaderSearchOffset() {
        if (!this.isInlineHeaderSearch) return;

        const headerSection = this.closest('.header-section');
        if (!headerSection) return;

        const { bottom } = headerSection.getBoundingClientRect();
        document.documentElement.style.setProperty('--header-search-offset', `${bottom}px`);
      }

      clearHeaderSearchOffset() {
        if (!this.isInlineHeaderSearch) return;

        document.documentElement.style.removeProperty('--header-search-offset');
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      open() {
        if (!this.isInlineHeaderSearch) return;

        document.body.classList.add('predictive-search-open');
        this.setHeaderSearchOffset();

        if (this.getQuery().length === 0) return;

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      close() {
        this.removeAttribute('results');
        this.removeAttribute('loading');
        this.abortController?.abort();

        if (this.isInlineHeaderSearch || this.closest('.collection')) {
          document.body.classList.remove('predictive-search-open');
        }

        if (this.isInlineHeaderSearch) {
          this.clearHeaderSearchOffset();

          if (!this.isMobileHeaderSearch || !document.body.classList.contains('header-mobile-search-open')) {
            this.input.blur();
          }
        }
      }

      onInputClick() {
        this.hideTypewriter();

        if (!this.isInlineHeaderSearch) return;
        if (document.body.classList.contains('predictive-search-open')) return;
        if (this.getQuery().length === 0) return;

        this.open();
      }

      onFocus(event) {
        if (this.isInlineHeaderSearch) {
          if (document.body.classList.contains('predictive-search-open')) return;
          if (this.getQuery().length === 0) return;

          this.open();
          return;
        }

        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          const url = this.buildUrl().toString();
          this.renderSection(url, event);
        }
      }

      onDocumentClick(event) {
        if (this.isMobileHeaderSearch) {
          if (!document.body.classList.contains('predictive-search-open')) return;

          const target = event.target;

          if (target.closest('.search__field--header-mobile, .search__box--header-mobile, .header__mobile-search-bar')) return;

          if (target.closest('.search__overlay--header-mobile')) {
            this.close();
          }

          return;
        }

        if (!this.isHeaderSearch) return;
        if (!document.body.classList.contains('predictive-search-open')) return;

        const target = event.target;

        if (target.closest('.search__field--header, .search__box--header')) return;

        if (target.closest('.search__overlay--header')) {
          this.close();
          return;
        }

        const headerSection = this.closest('.header-section');
        if (headerSection?.contains(target)) {
          this.close();
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.close();
        this.input.focus();
      }

      getQuery() {
        return this.input.value.trim();
      }

      buildUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);
        const configuredLimit = this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit'), 10) : 3;
        const displayLimit = Number.isNaN(configuredLimit) ? 3 : configuredLimit;
        let requestLimit = Math.max(displayLimit, displayLimit * 4);

        // Drawer and header need a larger pool because Liquid filters to in-stock products.
        if (this.classList.contains('search__form--drawer') || this.isInlineHeaderSearch) {
          requestLimit = Math.max(requestLimit, 60);
        }

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', requestLimit);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          if (this.isMobileHeaderSearch) {
            this.close();
            return;
          }

          this.clear();
          return;
        }

        this.hideTypewriter();

        if (this.isInlineHeaderSearch) {
          document.body.classList.add('predictive-search-open');
          this.setHeaderSearchOffset();
        }

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      onTabClick(event) {
        const tab = event.target.closest('[data-search-tab]');
        if (!tab || !this.contains(tab)) return;

        const panelName = tab.getAttribute('data-search-tab');

        this.querySelectorAll('[data-search-tab]').forEach((button) => {
          button.setAttribute('aria-selected', button === tab ? 'true' : 'false');
        });

        this.querySelectorAll('[data-search-panel]').forEach((panel) => {
          panel.hidden = panel.getAttribute('data-search-panel') !== panelName;
        });
      }

      renderSection(url) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);

        this.setAttribute('results', '');

        if (this.isInlineHeaderSearch) {
          this.setHeaderSearchOffset();
        }
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);

            this.removeAttribute('loading');
            this.setAttribute('results', '');

            if (this.isInlineHeaderSearch) {
              this.setHeaderSearchOffset();
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSearchResults(responseText) {
        const resultsContainer = this.querySelector('.search__results');
        if (!resultsContainer?.id) return;

        const updatedResults = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(resultsContainer.id);

        if (!updatedResults) return;

        resultsContainer.innerHTML = updatedResults.innerHTML;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          const form = this.closest('form[is="predictive-search"]');

          if (form?.classList.contains('search__form--header') || form?.classList.contains('search__form--header-mobile')) {
            form.close();
            return;
          }

          document.body.classList.remove('predictive-search-open');
          form?.removeAttribute('results');
        });
      }
    }
  );
}
